import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import WistiaPlayer from "../components/WistiaPlayer";
import CaseStudyHero from "../components/CaseStudyHero";
import { breadcrumbSchema } from "../lib/schemas";

// Anti-duplication / missing-file placeholder.
const SafeImage = ({ src, alt, className = "", aspectClass = "aspect-[4/3]" }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={`relative ${aspectClass} bg-dodgers/5 border-2 border-dashed border-lime flex items-center justify-center ${className}`}
        role="img"
        aria-label={`${alt} — placeholder`}
      >
        <span className="font-condensed uppercase tracking-widest text-[12px] text-dodgers/60">
          Foto in arrivo
        </span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`w-full h-full object-cover ${className}`}
    />
  );
};

// Card per i 4 asset dell'approccio
const ApproachCard = ({ num, title, body, testId }) => (
  <div className="border border-dodgers/15 p-7 md:p-8 h-full" data-testid={testId}>
    <p className="font-condensed uppercase tracking-widest text-[12px] text-mandrogni mb-3">
      ● {num}
    </p>
    <h3 className="font-display text-[22px] md:text-[26px] leading-tight mb-3">{title}</h3>
    <p className="text-base md:text-[17px] leading-[1.7] text-mandrogni">{body}</p>
  </div>
);

// Card per case correlati
const RelatedCard = ({ to, title, eyebrow }) => (
  <Link
    to={to}
    className="block border border-dodgers/15 p-6 md:p-7 hover:border-lime transition-colors group"
    data-testid={`related-${to.replace(/[^a-z0-9]+/g, "-")}`}
  >
    <p className="font-condensed uppercase tracking-widest text-[12px] text-mandrogni mb-2">
      {eyebrow}
    </p>
    <p className="font-display text-[20px] md:text-[22px] leading-tight group-hover:text-lime transition-colors">
      {title}
    </p>
    <span className="inline-block mt-4 font-condensed uppercase tracking-widest text-[12px] text-dodgers/60">
      Vedi il case →
    </span>
  </Link>
);

const WorkPradaAmericasCup = () => {
  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Prada × America's Cup 36", url: "/works/prada-americas-cup" },
  ]);

  return (
    <>
      <Seo
        title="Prada × America's Cup 36 Auckland 2021 | OnSports Case"
        description="Image coordinata Prada per la 36ª America's Cup di Auckland: video animation, app design, village set-up, merchandising. OnSports Content & IP Studio."
        path="/works/prada-americas-cup"
        image="/images/works/prada-americas-cup/hero.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/prada-americas-cup/hero.jpg"
        imageAlt="Prada America's Cup 36"
        eyebrow="● Case study · Cultural Impact · Business Impact"
        titleLines={[
          "Prada × America's Cup.",
          "L'evento sportivo più antico",
          "del mondo si EVOLVE.",
        ]}
        highlightWord="EVOLVE."
        subtitle="Image coordinata, video animation, design app, village set-up, merchandising per la 36ª edizione dell'America's Cup. Un brand secolare che incontra il futuro dell'alta vela."
        meta="2021  ·  Cliente — Prada  ·  Evento — 36° America's Cup  ·  Sede — Auckland (NZ)"
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="bg-galactico text-dodgers py-16 md:py-24" data-testid="section-overview">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● Overview</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[24ch]">
              Quando il brand più iconico del lusso italiano sponsorizza la regata più antica del mondo.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-mandrogni">
              <p>
                L'America's Cup è la regata sportiva più antica del mondo: prima edizione 1851, costa
                dell'Isola di Wight. Centosettant'anni dopo, nella 36ª edizione disputata ad Auckland
                nel marzo 2021, Prada arriva come Title Partner. Non un semplice sponsor, ma il brand
                che ridisegna l'identità visiva e l'esperienza di tutto l'evento, dal village allo
                schermo allo store.
              </p>
              <p>
                OnSports ha lavorato all'image coordinata completa: dall'animazione video che apre i
                broadcast, al design dell'app ufficiale dell'evento, dall'allestimento dei village in
                Nuova Zelanda al merchandising distribuito globalmente. Ogni touchpoint visivo
                costruito attorno a una grammatica precisa: alta vela come simbolo di eccellenza,
                Prada come grammatica visiva dell'eccellenza.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 3 — LA CITAZIONE */}
      <section className="bg-dodgers text-galactico py-20 md:py-28" data-testid="section-pullquote">
        <div className="container-os max-w-[720px] mx-auto text-center">
          <RevealOnScroll>
            <p
              className="font-display italic leading-[1.2]"
              style={{ fontSize: "clamp(28px, 4vw, 44px)" }}
            >
              "His majesty, there is no second."
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-6 font-condensed uppercase tracking-widest text-[13px] text-lime">
              Regata inaugurale dell'America's Cup, 1851
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <p className="mt-8 text-[15px] italic text-galactico/60 leading-[1.7] max-w-[58ch] mx-auto">
              Quando la Regina Vittoria chiese chi fosse arrivato secondo dopo la goletta America,
              le risposero così. Da allora, in America's Cup, non c'è medaglia d'argento.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 4 — LA SFIDA */}
      <section className="bg-galactico text-dodgers py-20 md:py-28" data-testid="section-sfida">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● La sfida</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[24ch]">
              Come si rinnova l'identità visiva di un evento che ha 170 anni?
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-mandrogni">
              <p>
                Il committment di Prada con l'America's Cup non è del 2021 — risale al 1997, anno
                della prima sponsorizzazione di Luna Rossa. La sfida della 36ª edizione era diversa:
                per la prima volta Prada non era solo sponsor di un team challenger, ma Title Partner
                dell'intero evento internazionale. Significava riscrivere la grammatica visiva di un
                evento globale mantenendo intatti i codici di un brand di alta moda secolare.
              </p>
              <p>
                Il rischio era doppio. Banalizzare l'identità Prada applicandola come decorazione su
                un format già esistente. O al contrario, far sembrare l'America's Cup uno showroom
                di moda invece di una competizione sportiva. La via era costruire una grammatica
                visiva che fosse riconoscibile come Prada in ogni singolo elemento, ma che servisse
                la narrazione sportiva — non viceversa.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 5 — IL VIDEO */}
      <section className="bg-dodgers text-galactico py-16 md:py-24" data-testid="section-video">
        <div className="container-os">
          <div className="max-w-[1100px] mx-auto">
            <WistiaPlayer mediaId="qw7brlr70i" aspect={16 / 9} />
          </div>
          <p className="mt-6 text-center text-[14px] italic text-galactico/60 max-w-[60ch] mx-auto">
            Video animation per il broadcast dell'evento — la grammatica visiva applicata al moving
            image.
          </p>
        </div>
      </section>

      {/* SEZ. 6 — L'APPROCCIO */}
      <section className="bg-galactico text-dodgers py-20 md:py-28" data-testid="section-approccio">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto">
            <RevealOnScroll>
              <SectionEyebrow>● L'approccio</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2 className="display-h2 mt-6">Quattro discipline, una sola grammatica.</h2>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <p className="mt-8 text-[17px] leading-[1.7] text-mandrogni">
                Il progetto si è articolato su quattro asset paralleli, tutti governati dalla stessa
                identità visiva sviluppata insieme al team creative Prada. Ognuno aveva esigenze
                tecniche e operative diverse, ma doveva restituire una sensazione visiva unica e
                riconoscibile.
              </p>
            </RevealOnScroll>
          </div>

          <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproachCard
                num="01"
                title="Video Animation"
                body="Animazione per l'apertura del broadcast dell'evento. Tipografia in movimento, transizioni costruite sul ritmo della vela, lavoro frame-by-frame su sequenze chiave. Pensata per essere riconoscibile dopo 2 secondi sullo schermo."
                testId="approach-01"
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <ApproachCard
                num="02"
                title="App Design"
                body="Interfaccia dell'app ufficiale dell'evento. Live tracking delle regate, classifiche, news, mappe del village. UI/UX completa pensata per essere visivamente Prada ma funzionalmente sportiva."
                testId="approach-02"
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <ApproachCard
                num="03"
                title="Village Set-Up"
                body="Allestimento dei village dell'evento ad Auckland. Direzione artistica della segnaletica, dei punti di accoglienza, delle vetrine espositive. Coordinazione visiva di tutti gli elementi fisici dell'esperienza in loco."
                testId="approach-03"
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <ApproachCard
                num="04"
                title="Merchandising"
                body="Linea merchandising ufficiale dell'evento. Design dei prodotti, packaging, sistema espositivo per i temporary store di Auckland e per la distribuzione internazionale online."
                testId="approach-04"
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 7 — GALLERIA */}
      <section className="bg-dodgers text-galactico py-16 md:py-24" data-testid="section-gallery">
        <div className="container-os">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SafeImage
              src="/images/works/prada-americas-cup/gallery-01.jpg"
              alt="Prada America's Cup — apparel maschile"
              aspectClass="aspect-square"
            />
            <SafeImage
              src="/images/works/prada-americas-cup/gallery-02.jpg"
              alt="Prada America's Cup — polo e giacca dettaglio"
              aspectClass="aspect-square"
            />
            <SafeImage
              src="/images/works/prada-americas-cup/gallery-03.jpg"
              alt="Prada America's Cup — Leave Nothing To Chance"
              aspectClass="aspect-square"
            />
            <SafeImage
              src="/images/works/prada-americas-cup/gallery-04.jpg"
              alt="Prada America's Cup — sistema editoriale e mobile"
              aspectClass="aspect-square"
            />
          </div>
        </div>
      </section>

      {/* SEZ. 8 — L'IMPATTO */}
      <section className="bg-dodgers text-galactico py-24 md:py-32" data-testid="section-impatto">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto text-center">
            <RevealOnScroll>
              <SectionEyebrow>● L'impatto</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2 className="display-h2 mt-6 max-w-[22ch] mx-auto">
                Un brand secolare. Una grammatica nuova. Una stagione{" "}
                <span className="text-lime">MEMORABILE</span>.
              </h2>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <p className="mt-8 text-[17px] leading-[1.7] text-galactico/85">
                La 36ª America's Cup è stata trasmessa in oltre 200 paesi. È stata la prima edizione
                completamente disputata su barche AC75 — monoscafi volanti che hanno ridefinito il
                modo in cui si naviga in regata. Prada ha attraversato questo cambiamento storico
                mantenendo intatta la sua riconoscibilità di brand. L'image coordinata sviluppata
                da OnSports è stato uno degli strumenti con cui questo posizionamento è stato
                costruito visivamente, evento per evento, asset per asset, dall'allestimento dei
                village ai broadcast globali.
              </p>
            </RevealOnScroll>
          </div>

          {/* Soft KPI */}
          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 max-w-[900px] mx-auto">
            {[
              { label: "Edizione America's Cup", value: "36ª", caption: "170 anni dalla prima" },
              { label: "Sede", value: "Auckland", caption: "Nuova Zelanda" },
              { label: "Trasmessa in", value: "200+", caption: "paesi" },
            ].map((kpi) => (
              <div key={kpi.label} className="text-center md:text-left">
                <p className="font-condensed uppercase tracking-widest text-[13px] text-lime mb-3">
                  {kpi.label}
                </p>
                <p
                  className="font-display leading-none"
                  style={{ fontSize: "clamp(32px, 4vw, 40px)" }}
                >
                  {kpi.value}
                </p>
                <p className="mt-2 text-[14px] text-galactico/60">{kpi.caption}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 9 — CREDITS */}
      <section className="bg-galactico text-dodgers py-16 md:py-24" data-testid="section-credits">
        <div className="container-os max-w-[720px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="font-display leading-tight mt-6" style={{ fontSize: "clamp(28px, 3.5vw, 36px)" }}>
              Chi ha fatto cosa.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <dl className="mt-10 divide-y divide-dodgers/15">
              {[
                ["Creative Direction & Image Coordinata", "OnSports + Prada Creative Team"],
                ["Video Animation", "OnSports"],
                ["App Design", "OnSports"],
                ["Village Set-Up", "OnSports + Partner locali Auckland"],
                ["Merchandising Design", "OnSports"],
                ["Cliente", "Prada"],
                ["Evento", "America's Cup 36"],
                ["Anno", "2021"],
              ].map(([label, value]) => (
                <div key={label} className="py-4 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-2 md:gap-6">
                  <dt className="font-condensed uppercase tracking-widest text-[13px] text-mandrogni">
                    {label}
                  </dt>
                  <dd className="text-[16px]">{value}</dd>
                </div>
              ))}
            </dl>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-32 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os max-w-[760px]">
          <RevealOnScroll>
            <h2 className="font-display leading-tight" style={{ fontSize: "clamp(28px, 3.5vw, 36px)" }}>
              Hai un evento globale o un progetto editoriale di scala? Costruiamo insieme la{" "}
              <span className="text-lime">PIATTAFORMA</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.08}>
            <p className="mt-6 text-[16px] md:text-[17px] leading-[1.7] text-galactico/85 max-w-[600px]">
              Una call iniziale di 30 minuti per capire se l'identità visiva del tuo progetto può
              diventare un asset durevole.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.16}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-other-content">
                <Button variant="secondary-light">Vedi altri case Content & IP</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 11 — NAVIGATION */}
      <section className="bg-galactico text-dodgers py-16 md:py-20" data-testid="section-related">
        <div className="container-os">
          <RevealOnScroll>
            <p className="font-condensed uppercase tracking-widest text-[13px] text-mandrogni mb-8">
              ● Altri case correlati
            </p>
          </RevealOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            <RelatedCard
              to="/works/reale-mutua-essere-libero"
              eyebrow="Cultural Impact · Content & IP"
              title="Reale Mutua × Essere Libero"
            />
            <RelatedCard
              to="/works/volleyball-world-italia"
              eyebrow="Cultural Impact · Social & Content"
              title="Volleyball World Italia"
            />
            <RelatedCard
              to="/works/nike-jd"
              eyebrow="Cultural Impact · Content & Performance"
              title="Nike × JD Sports"
            />
          </div>
          <div className="mt-10">
            <Link
              to="/works"
              className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-[13px] text-dodgers hover:text-lime transition-colors"
              data-testid="back-to-works"
            >
              <ArrowLeft size={16} /> Tutti i casi studio
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkPradaAmericasCup;
